import { useRef, useEffect, useState } from 'react';
import ScrollReveal from '@/components/shared/ScrollReveal';
import {
  ArrowRight,
  Users,
  Repeat,
  Network,
  Headset,
  BriefcaseBusiness,
  Play,
} from 'lucide-react';

const WHATSAPP_LICENSE =
  'https://wa.me/5551996777625?text=Ol%C3%A1!%20Tenho%20interesse%20em%20me%20tornar%20um%20licenciado%20iGreen%20e%20gostaria%20de%20entender%20como%20funciona%20a%20oportunidade%20e%20a%20renda%20recorrente.';

const LICENSE_SIGNUP_URL =
  'https://expansao.igreenenergy.com.br/?id=30942&checkout=true&tipolicenca=CONNECT,';

const steps = [
  {
    number: '01',
    icon: Users,
    title: 'Conecte clientes',
    description: 'Apresente os serviços iGreen e conecte mais pessoas às nossas soluções.',
  },
  {
    number: '02',
    icon: Repeat,
    title: 'Gere recorrência',
    description: 'Receba comissões recorrentes enquanto seus clientes permanecem ativos.',
  },
  {
    number: '03',
    icon: Network,
    title: 'Desenvolva sua rede',
    description: 'Expanda sua rede de contatos e multiplique suas oportunidades.',
  },
];

function StepCard({ step, index }: { step: typeof steps[0]; index: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`group relative flex h-full flex-col items-center text-center sm:items-start sm:text-left bg-background-900/60 backdrop-blur-sm rounded-2xl border border-background-800/50 p-4 md:p-5 transition-all duration-500 hover:-translate-y-1 hover:border-primary-500/40 hover:bg-background-900/80 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
      }`}
      style={{ transitionDelay: `${index * 120}ms` }}
    >
      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold border border-primary-500/30 text-primary-400 bg-primary-500/10 mb-5">
        {step.number}
      </span>

      <div className="w-14 h-14 rounded-xl bg-primary-500/15 ring-1 ring-primary-500/30 flex items-center justify-center text-primary-400 mb-4 transition-transform duration-300 group-hover:scale-110">
        <step.icon className="w-7 h-7" />
      </div>

      <h3 className="text-lg md:text-xl font-bold text-white mb-2">{step.title}</h3>
      <p className="text-sm text-foreground-400 leading-relaxed">{step.description}</p>
    </div>
  );
}

export default function LicenseSection() {
  const [videoStarted, setVideoStarted] = useState(false);

  return (
    <section id="licenciado" className="immersive-panel bg-background-950">
      {/* Background effects */}
      <div className="absolute inset-0 bg-radial-green pointer-events-none opacity-40" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-radial-green-sm rounded-full pointer-events-none opacity-30" />

      {/* Subtle grid */}
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage:
            'linear-gradient(oklch(var(--primary-500) / 0.15) 1px, transparent 1px), linear-gradient(90deg, oklch(var(--primary-500) / 0.15) 1px, transparent 1px)',
          backgroundSize: '80px 80px',
        }}
      />

      <div className="split-viewport">
          <ScrollReveal className="split-viewport__content order-2 lg:order-1 text-center lg:text-left">
            <span className="inline-flex w-auto self-center lg:self-start items-center gap-2 px-4 py-1.5 rounded-full bg-primary-500/10 border border-primary-500/20 text-primary-400 text-sm font-semibold mb-5">
              <BriefcaseBusiness className="w-4 h-4" />
              Seja um Licenciado
            </span>

            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-extrabold text-white tracking-tighter leading-[1.1]">
              <span className="block">Construa uma renda</span>
              <span className="block"><span className="text-gradient">recorrente</span> com a</span>
              <span className="block">iGreen Energy</span>
            </h2>

            <p className="mt-5 text-lg text-foreground-400 leading-relaxed">
              Conecte clientes aos serviços iGreen e desenvolva uma fonte de renda recorrente conforme as regras comerciais vigentes.
            </p>

            {/* Steps */}
            <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-3 lg:grid-cols-3">
              {steps.map((step, i) => (
                <StepCard key={step.number} step={step} index={i} />
              ))}
            </div>

            {/* Support card */}
            <div className="mt-4 flex flex-col items-center gap-4 p-4 text-center sm:flex-row sm:text-left rounded-2xl bg-primary-500/10 border border-primary-500/25">
              <div className="w-14 h-14 rounded-xl bg-primary-500 glow-green-sm flex items-center justify-center text-background-50 shrink-0">
                <Headset className="w-8 h-8" />
              </div>
              <div>
                <h4 className="text-base md:text-lg font-bold text-white mb-0.5">
                  Treinamentos, ferramentas e suporte
                </h4>
                <p className="text-sm text-foreground-300 leading-relaxed">
                  Tudo de que você precisa para crescer com segurança e resultados.
                </p>
              </div>
            </div>

            {/* CTA + disclaimer */}
            <div className="mt-6">
              <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                <a
                  href={LICENSE_SIGNUP_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-primary w-full sm:w-auto text-base px-8 py-4 group"
                >
                  Faça seu cadastro agora
                  <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                </a>
                <a
                  href={WHATSAPP_LICENSE}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-ghost w-full sm:w-auto text-base px-8 py-4 group"
                >
                  Conhecer a oportunidade
                  <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                </a>
              </div>
              <p className="mt-4 text-xs text-foreground-600 leading-relaxed max-w-xl mx-auto lg:mx-0">
                Resultados variam conforme atividade, clientes ativos e regras comerciais.
              </p>
            </div>
          </ScrollReveal>

          <ScrollReveal className="split-viewport__media order-1 lg:order-2" delay={120}>
            <div className="relative h-full overflow-hidden bg-black">
              {videoStarted ? (
                <iframe
                  src="https://www.youtube-nocookie.com/embed/qdeblguZdGc?rel=0&autoplay=1"
                  title="Institucional iGreen 2026"
                  className="absolute inset-0 h-full w-full border-0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                />
              ) : (
                <button
                  type="button"
                  onClick={() => setVideoStarted(true)}
                  className="group absolute inset-0 h-full w-full cursor-pointer overflow-hidden text-white"
                  aria-label="Reproduzir o vídeo Institucional iGreen 2026"
                >
                  <img
                    src={`${__BASE_PATH__}assets/video-institucional-thumb.png?v=2`}
                    alt="Capa do vídeo Institucional iGreen 2026"
                    className="absolute inset-0 h-full w-full scale-[1.08] object-cover object-[center_55%]"
                    width="831"
                    height="1891"
                  />
                  <span className="absolute inset-0 bg-black/10 transition-colors duration-300 group-hover:bg-black/20" />
                  <span className="absolute left-1/2 top-1/2 flex h-20 w-20 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-primary-500 shadow-[0_0_32px_8px] shadow-primary-500/35 transition-transform duration-300 group-hover:scale-110 md:h-24 md:w-24">
                    <Play className="ml-1 h-9 w-9 fill-current md:h-11 md:w-11" />
                  </span>
                </button>
              )}
            </div>
          </ScrollReveal>
      </div>
    </section>
  );
}
